import { useFormik } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { FaRegTrashAlt } from 'react-icons/fa';
import { FaEuroSign } from 'react-icons/fa6';
import { HiOutlineInformationCircle } from 'react-icons/hi2';
import { MdAdd } from 'react-icons/md';
import { useParams } from 'react-router-dom';
import { Button, Input, Switch, Textarea, Tooltip, cn } from 'rizzui';
import Swal from 'sweetalert2';
import { toast } from 'sonner';
import * as yup from 'yup';
import CustomSelect from '../../../../../components/forms/CustomSelect';
import FormLabel from '../../../../../components/forms/FormLabel';
import RequiredInput from '../../../../../components/forms/InputHOC';
import usePaymentMethods from '../../../../../hooks/api-calls/expirations/usePaymentMethods';
import useUsers from '../../../../../hooks/api-calls/useUsers';
import useFetch from '../../../../../hooks/useFetch';
import useHandleErrors from '../../../../../hooks/useHandleErrors';
import { ExpirationService } from '../../../../../services/expiration/expirationService';
import { InvoiceService } from '../../../../../services/invoice/invoiceService';
import { InvoiceApiResponse } from '../../../../../type/entities/invoice-type';
import { fixNumber } from '../../../../../utils/formatNumber';
import InvoiceProfileLayout from '../../InvoiceProfileLayout';
import { usePrivilege } from '../../../../../components/priviledge/PriviledgeProvider';

interface InvoiceExpirationsProps { }

const schema = yup.object({
    invoiceId: yup.string().required('La factura es obligatoria'),
    contractId: yup.string().nullable().notRequired(),
    expirations: yup.array().of(yup.object().shape({
        date: yup.string().required('La fecha de vencimiento es obligatoria'),
        amount: yup.number().required('El importe es obligatorio'),
        description: yup.string().required('La descripción es obligatoria'),
        paymentMethodId: yup.string().required('El método de pago es obligatorio'),
        reminderDate: yup.string().nullable().notRequired(),
        reminderResponsibleId: yup.string().when('reminderDate', {
            is: (value: any) => (value === null || value === undefined || value === ''),
            then: (schema) => schema.notRequired(),
            otherwise: (schema) => schema.required('El responsable del recordatorio es obligatorio si hay fecha de recordatorio'),
        }),
        notes: yup.string().nullable().notRequired(),
        deleted: yup.boolean().notRequired(),
    })),
});

const getFormikErrorMessages = (errors: any): string[] => {
    if (!errors) return [];

    if (typeof errors === 'string') {
        return [errors];
    }

    if (Array.isArray(errors)) {
        return errors.flatMap((error) => getFormikErrorMessages(error));
    }

    if (typeof errors === 'object') {
        return Object.values(errors).flatMap((error) => getFormikErrorMessages(error));
    }

    return [];
};

const InvoiceExpirations: React.FC<InvoiceExpirationsProps> = () => {

    const { id = '' } = useParams<{ id: string }>();
    const { handleErrors } = useHandleErrors();
    const { getUsersList } = useUsers({}, true);
    const { getPaymentMethodsList } = usePaymentMethods(true);
    const service = new InvoiceService();
    const { userCan } = usePrivilege();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [contractPaymentMethodSync, setContractPaymentMethodSync] = useState<{
        enabled: boolean;
        paymentMethodId: string | null;
    }>({
        enabled: false,
        paymentMethodId: null,
    });
    const [expiration, setExpiration] = useState({
        date: '',
        amount: 0,
        description: '',
        paymentMethodId: '',
        reminderDate: '',
        reminderResponsibleId: '',
        notes: '',
        deleted: false,
    });

    const [data, loading] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getInvoiceById(id);
        return response.getResponseData() as InvoiceApiResponse;
    }, [id]));

    const contractPaymentMethodId = data?.contract?.paymentMethods?.id || '';
    const contractPaymentMethodName = data?.contract?.paymentMethods?.name || 'método del contrato';

    const applyExpirationPaymentMethodChange = async (expirationId: string, paymentMethodId: string) => {
        const baseExpirations = formik.values.expirations ?? [];
        const baseUpdatedExpirations = baseExpirations.map((item: any) => (
            item.id === expirationId ? { ...item, paymentMethodId } : item
        ));

        if (!data?.contract?.id || !contractPaymentMethodId || paymentMethodId === contractPaymentMethodId) {
            setContractPaymentMethodSync({ enabled: false, paymentMethodId: null });
            formik.setFieldValue('expirations', baseUpdatedExpirations);
            return;
        }

        const selectedPaymentMethodName = getPaymentMethodsList().find((item: any) => item.value === paymentMethodId)?.label || 'método seleccionado';

        const result = await Swal.fire({
            title: 'Método distinto al del contrato',
            html: `
                <div style="text-align:left">
                    <p>El vencimiento quedará con un método de pago distinto al del contrato.</p>
                    <p><strong>Contrato:</strong> ${contractPaymentMethodName}</p>
                    <p><strong>Nuevo método:</strong> ${selectedPaymentMethodName}</p>
                    <p>¿Quieres aplicar también este cambio al contrato y al resto de vencimientos no pagados?</p>
                </div>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, propagar cambio',
            cancelButtonText: 'No, solo este vencimiento',
            confirmButtonColor: '#009737',
        });

        if (result.isConfirmed) {
            const propagatedExpirations = baseExpirations.map((item: any) => {
                if (item.deleted || item.paid) {
                    return item;
                }

                return { ...item, paymentMethodId };
            });

            setContractPaymentMethodSync({
                enabled: true,
                paymentMethodId,
            });
            formik.setFieldValue('expirations', propagatedExpirations);
            return;
        }

        setContractPaymentMethodSync({ enabled: false, paymentMethodId: null });
        formik.setFieldValue('expirations', baseUpdatedExpirations);
    };

    const submit = async (values: any) => {
        setIsLoading(true);
        try {
            const response = await (await (new ExpirationService).editExpiration({
                ...values,
                syncContractPaymentMethod: contractPaymentMethodSync.enabled,
                contractPaymentMethodId: contractPaymentMethodSync.paymentMethodId,
            })).getResponseData();
            if (response.success) {
                toast.success(response?.message || "Vencimiento editado correctamente");
            } else {
                handleErrors(response);
            }
        } catch (error: any) {
            toast.error('Error al editar los vencimientos de la factura');
        } finally {
            setIsLoading(false);
        }
    };

    const handleStatusChange = async (expirationId: string, paid: boolean) => {
        try {
            const response = await (await (new ExpirationService).markAsPaid(expirationId)).getResponseData();
            if (response.success) {
                toast.success(response.message || "Estado actualizado correctamente");
            } else {
                handleErrors(response);
                formik.setFieldValue(
                    'expirations',
                    formik.values.expirations?.map((exp: any) => exp.id === expirationId ? { ...exp, paid: !paid } : exp)
                );
            }
        } catch (error) {
            toast.error('Error al actualizar el estado');
            formik.setFieldValue(
                'expirations',
                formik.values.expirations?.map((exp: any) => exp.id === expirationId ? { ...exp, paid: !paid } : exp)
            );
        }
    };

    const formik = useFormik({
        initialValues: {
            invoiceId: data?.expirations[0]?.invoice?.id || '',
            contractId: data?.expirations[0]?.contract?.id || data?.contract?.id || '',
            expirations: data?.expirations
                ? data.expirations.map((ex: any) => ({
                    date: ex.expirationDate?.date.split(' ')[0] || '',
                    amount: ex.amount || 0,
                    description: ex.description || '',
                    paymentMethodId: ex.paymentMethod?.id || '',
                    reminderDate: ex.reminderDate?.date.split(' ')[0] || '',
                    reminderResponsibleId: ex.reminderResponsible?.id || '',
                    notes: ex.notes || '',
                }))
                : [],
        },
        validationSchema: schema,
        validateOnBlur: false,
        validateOnChange: false,
        onSubmit: values => { submit(values) },
    });

    const handleSave = async () => {
        const errors = await formik.validateForm();

        if (Object.keys(errors).length > 0) {
            const errorMessages = getFormikErrorMessages(errors);
            toast.error(errorMessages[0] || 'Revisa los datos de los vencimientos antes de guardar');
            return;
        }

        formik.handleSubmit();
    };

    useEffect(() => {
        if (!data) return;
        formik.setValues({
            invoiceId: data?.expirations[0]?.invoice?.id || id || '',
            contractId: data?.expirations[0]?.contract?.id || data?.contract?.id || '',
            expirations: data.expirations.map((ex: any) => ({
                id: ex.id || '',
                date: ex.expirationDate?.date.split(' ')[0] || '',
                amount: ex.amount || 0,
                description: ex.description || '',
                paymentMethodId: ex.paymentMethod?.id || '',
                reminderDate: ex.reminderDate?.date.split(' ')[0] || '',
                reminderResponsibleId: ex.reminderResponsible?.id || '',
                notes: ex.notes || '',
                deleted: ex.isDeleted || false,
                paid: ex.isPaid || false,
            })),
        });
        setContractPaymentMethodSync({ enabled: false, paymentMethodId: null });
    }, [data]);

    const canChangeExpirationStatus = userCan('edit_expirations', 'expirations');
    const canCreateExpiration = userCan('create_expirations', 'expirations');
    const canDeleteExpiration = userCan('delete_expirations', 'expirations');

    return (
        <InvoiceProfileLayout isLoading={loading}>
            <div className="@container">
                {formik.values.expirations?.map((ex: any, index: number) => (
                    <div key={ex.id + index} className={cn("grid md:grid-cols-5 gap-4 py-3 px-4 mt-4 mb-8 bg-primary-0 border border-gray-300 rounded", { 'bg-red-50': ex.deleted })}>
                        <div className='md:col-span-5 flex justify-end items-center'>
                            <Switch
                                id={`expirations[${index}].paid`}
                                label="Pagado"
                                checked={ex.paid}
                                disabled={!canChangeExpirationStatus}
                                onChange={(e: any) => {
                                    const updatedExpirations = formik.values.expirations?.map((exp: any) => {
                                        if (exp.id === ex.id) {
                                            return { ...exp, paid: e.target.checked };
                                        }
                                        return exp;
                                    });
                                    formik.setFieldValue('expirations', updatedExpirations);
                                    handleStatusChange(ex.id, e.target.checked);
                                }}
                            />
                        </div>

                        <RequiredInput
                            id={`expirations[${index}].description`}
                            label="Descripción"
                            formik={formik}
                            disabled={!canChangeExpirationStatus}
                            value={ex.description}
                            //error={formik.errors.expirations && formik.errors.expirations[index]?.description}
                            className="md:col-span-2"
                        />

                        <RequiredInput
                            id={`expirations[${index}].amount`}
                            type="number"
                            label="Importe"
                            formik={formik}
                            disabled={!canChangeExpirationStatus}
                            value={ex.amount}
                            //error={formik.errors.expirations && formik.errors.expirations[index]?.amount}
                            suffix={<FaEuroSign />}
                        />

                        <CustomSelect
                            isSearchable
                            isClearable
                            id={`expirations[${index}].paymentMethodId`}
                            label="Método de Pago"
                            required
                            isDisabled={!canChangeExpirationStatus}
                            options={getPaymentMethodsList()}
                            onChange={(e: any) => applyExpirationPaymentMethodChange(ex.id, e ? e.value : '')}
                            value={ex.paymentMethodId ? { value: ex.paymentMethodId, label: getPaymentMethodsList().find((c: any) => c.value === ex.paymentMethodId)?.label } : null}
                        //error={formik.errors.expirations && formik.errors.expirations[index]?.paymentMethodId}
                        />

                        <RequiredInput
                            id={`expirations[${index}].date`}
                            disabled={!canChangeExpirationStatus}
                            type="date"
                            label="Fecha de Vencimiento"
                            formik={formik}
                            value={ex.date}
                            required={!!ex.reminderResponsibleId}
                        //error={formik.errors.expirations && formik.errors.expirations[index]?.date}
                        />

                        <RequiredInput
                            id={`expirations[${index}].reminderDate`}
                            disabled={!canChangeExpirationStatus}
                            type="date"
                            label="Fecha de Recordatorio"
                            required={!!ex.reminderResponsibleId}
                            formik={formik}
                            value={ex.reminderDate}
                            clearable
                            onClear={() => {
                                const updatedExpirations = formik.values.expirations?.map((e: any) => {
                                    if (e.id === ex.id) {
                                        return { ...e, reminderDate: '' };
                                    }
                                    return e;
                                });
                                formik.setFieldValue('expirations', updatedExpirations);
                            }}
                        //error={formik.errors.expirations && formik.errors.expirations[index]?.reminderDate}
                        />

                        <div className="space-y-2">
                            <CustomSelect
                                isSearchable
                                isClearable
                                isDisabled={!canChangeExpirationStatus || !userCan('get_users_for_select', 'user')}
                                id={`expirations[${index}].reminderResponsibleId`}
                                label="Responsable del Recordatorio"
                                required={!!ex.reminderDate}
                                options={getUsersList()}
                                onChange={(e: any) => {
                                    const updatedExpirations = formik.values.expirations?.map((exp: any) => {
                                        if (exp.id === ex.id) {
                                            return { ...exp, reminderResponsibleId: e?.value };
                                        }
                                        return exp;
                                    });
                                    formik.setFieldValue('expirations', updatedExpirations);
                                }}
                                value={ex.reminderResponsibleId ? { value: ex.reminderResponsibleId, label: getUsersList().find((c: any) => c.value === ex.reminderResponsibleId)?.label } : null}
                            />
                            {!userCan('get_users_for_select', 'user') && (
                                <div className="flex items-center gap-2 text-red-500 text-sm">
                                    <HiOutlineInformationCircle size={18} className="flex-shrink-0" />
                                    <span>No tienes permisos para ver los usuarios</span>
                                </div>
                            )}
                        </div>

                        <Textarea
                            id={`expirations[${index}].notes`}
                            label="Notas"
                            disabled={!canChangeExpirationStatus}
                            value={ex.notes}
                            onChange={(e: any) => {
                                const updatedExpirations = formik.values.expirations?.map((expiration: any) => {
                                    if (expiration.id === ex.id) {
                                        return { ...expiration, notes: e.target.value };
                                    }
                                    return expiration;
                                });
                                formik.setFieldValue('expirations', updatedExpirations);
                            }}
                            //error={formik.errors.expirations && formik.errors.expirations[index]?.notes}
                            rows={3}
                            className="md:col-span-3"
                        />

                        <div className={cn("md:col-span-5 flex justify-center items-end", { "hidden": ex.deleted })}>
                            <Button
                                color="danger"
                                size='sm'
                                disabled={!canDeleteExpiration}
                                onClick={() => {
                                    // Set deleted to true
                                    const updatedExpirations = formik.values.expirations?.map((e: any) => {
                                        if (e.id === ex.id) {
                                            return { ...e, deleted: true };
                                        }
                                        return e;
                                    });
                                    formik.setFieldValue('expirations', updatedExpirations);
                                }}
                            >
                                <FaRegTrashAlt />
                            </Button>
                        </div>
                    </div>
                ))}

                {canCreateExpiration && (
                    <div className="grid md:grid-cols-5 gap-4 py-3 px-4 mt-4 mb-10 border border-gray-300 rounded">
                        <Input
                            id='description'
                            label={<FormLabel label="Descripción" required />}
                            value={expiration.description}
                        onChange={(e: any) => setExpiration({ ...expiration, description: e.target.value })}
                        className="md:col-span-2"
                        />

                        <Input
                            id='amount'
                            type="number"
                            label={<FormLabel label="Importe" required />}
                            suffix={<FaEuroSign />}
                            value={expiration.amount}
                            onChange={(e: any) => setExpiration({ ...expiration, amount: Number(e.target.value) })}
                        />

                        <CustomSelect
                            isSearchable
                            isClearable
                            id={'paymentMethodId'}
                            label="Método de Pago"
                            required
                            options={getPaymentMethodsList()}
                            onChange={(e: any) => setExpiration({ ...expiration, paymentMethodId: e ? e.value : '' })}
                            value={expiration.paymentMethodId ? { value: expiration.paymentMethodId, label: getPaymentMethodsList().find((c: any) => c.value === expiration.paymentMethodId)?.label } : null}
                        />

                        <Input
                            id='date'
                            type="date"
                            label={<FormLabel label="Fecha de Vencimiento" required />}
                            value={expiration.date}
                            onChange={(e: any) => setExpiration({ ...expiration, date: e.target.value })}
                        />

                        <Input
                            id='reminderDate'
                            type="date"
                            label={<FormLabel label="Fecha de Recordatorio" required={!!expiration.reminderResponsibleId} />}
                            value={expiration.reminderDate}
                            onChange={(e: any) => setExpiration({ ...expiration, reminderDate: e.target.value })}
                            clearable
                            onClear={() => setExpiration({ ...expiration, reminderDate: '' })}
                        />

                        <div className="space-y-2">
                            <CustomSelect
                                isSearchable
                                isClearable
                                required={!!expiration.reminderDate}
                                id={'reminderResponsibleId'}
                                label="Responsable del Recordatorio"
                                options={getUsersList()}
                                onChange={(e: any) => setExpiration({ ...expiration, reminderResponsibleId: e ? e.value : '' })}
                                value={expiration.reminderResponsibleId ? { value: expiration.reminderResponsibleId, label: getUsersList().find((c: any) => c.value === expiration.reminderResponsibleId)?.label } : null}
                                isDisabled={!userCan('get_users_for_select', 'user')}
                            />
                            {!userCan('get_users_for_select', 'user') && (
                                <div className="flex items-center gap-2 text-red-500 text-sm">
                                    <HiOutlineInformationCircle size={18} className="flex-shrink-0" />
                                    <span>No tienes permisos para ver los usuarios</span>
                                </div>
                            )}
                        </div>

                        <Textarea
                            id='notes'
                            label="Notas"
                            value={expiration.notes}
                            onChange={(e: any) => setExpiration({ ...expiration, notes: e.target.value })}
                            className="md:col-span-3"
                            rows={3}
                        />

                        <div className="md:col-span-5 flex justify-center items-end">
                            <Button
                                color='secondary'
                                size='sm'
                                disabled={expiration.date === '' || expiration.amount === 0 || expiration.description === '' || expiration.paymentMethodId === '' || (expiration.reminderDate !== '' && expiration.reminderResponsibleId === '') || (expiration.reminderResponsibleId !== '' && expiration.reminderDate === '')}
                                onClick={() => {
                                    formik.setFieldValue('expirations', [...formik.values.expirations, expiration]);
                                    setExpiration({ date: '', amount: 0, description: '', paymentMethodId: '', reminderDate: '', reminderResponsibleId: '', notes: '', deleted: false });
                                }}
                            >
                                <MdAdd className='h-5 w-5' />
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            <div className="fixed bottom-0 left-0 right-0 bg-white px-4 pt-2 pb-4 shadow-lg flex justify-end items-center">
                <div className="text-right me-2 flex items-center">
                    <span className="text-lg font-semibold me-3">
                        Total: {fixNumber(formik.values.expirations?.filter((ex: any) => !ex.deleted).reduce((acc: number, ex: any) => acc + ex.amount, 0))} €
                    </span>

                    <span
                        className={cn("text-lg font-semibold", {
                            "text-red-500": (data?.totalPrice || 0) - formik.values.expirations?.filter((ex: any) => !ex.deleted).reduce((acc: number, ex: any) => acc + ex.amount, 0) !== 0,
                        })}
                    >
                        Pendiente: {fixNumber((data?.totalPrice || 0) - formik.values.expirations?.filter((ex: any) => !ex.deleted).reduce((acc: number, ex: any) => acc + ex.amount, 0))} €
                    </span>
                </div>

                <Tooltip
                    content={'El importe total de la suma de los importes de los vencimientos debe coincidir con el total de la factura'}
                    className="text-sm"
                    color="invert"
                >
                    <div className={'inline-block ms-1'}>
                        <HiOutlineInformationCircle size={20} color={'primary'} />
                    </div>
                </Tooltip>

                {canChangeExpirationStatus && (
                    <Button
                        color='danger'
                        size='lg'
                        onClick={handleSave}
                        isLoading={isLoading}
                        disabled={isLoading || (data?.totalPrice || 0) - formik.values.expirations?.filter((ex: any) => !ex.deleted).reduce((acc: number, ex: any) => acc + ex.amount, 0) !== 0}
                        className='ms-4'
                    >
                    Guardar
                </Button>
                )}
            </div>
        </InvoiceProfileLayout>
    );
};

export default InvoiceExpirations;
